import { UserFacingError } from '../user-facing-error.js';
import {
  invalidateAccessToken,
  refreshAccessToken,
  session,
  SessionExpiredError,
} from './session.js';

const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';

/**
 * One error envelope, matching the API's:
 *
 *   { error: { code, message, details?, requestId } }
 *
 * `details` carries the per-field ingest failures, each with a JSON Pointer
 * into the submitted record — which is what lets the A–B–C form highlight
 * exactly the fields that were rejected rather than showing one blanket
 * message.
 */
export interface FieldFailure {
  rule: string;
  /** JSON Pointer, e.g. `/consent_and_ethics/provocation_used`. */
  path: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: FieldFailure[] | undefined,
    readonly requestId: string | undefined,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The submission was rejected by the ingest gate, with per-field reasons. */
  get isIngestRejection(): boolean {
    return this.code === 'ingest_rejected';
  }

  /** Failures for one JSON Pointer prefix, for rendering beside a field. */
  failuresFor(pathPrefix: string): FieldFailure[] {
    return (this.details ?? []).filter((f) => f.path.startsWith(pathPrefix));
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Public endpoints (terms, ethograms) do not need a token. */
  anonymous?: boolean;
  signal?: AbortSignal;
}

/**
 * The single fetch path for the whole app.
 *
 * Handles auth, the error envelope, and exactly one retry after a token
 * refresh. Every refresh goes through `refreshAccessToken`, which single-flights
 * them — see the note in session.ts about why that matters.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, anonymous = false, signal } = options;

  const send = async (token: string | null): Promise<Response> =>
    fetch(`${API_BASE}${path}`, {
      method,
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    });

  let token: string | null = null;
  if (!anonymous) {
    try {
      token = await session.getValidAccessToken();
    } catch (error) {
      if (error instanceof SessionExpiredError) session.clear();
      throw error;
    }
  }

  let response = await send(token);

  // Exactly one retry. A second 401 after a fresh token means the token is not
  // the problem, and retrying again would loop.
  if (response.status === 401 && !anonymous) {
    invalidateAccessToken();
    try {
      token = await refreshAccessToken();
    } catch (error) {
      session.clear();
      throw error;
    }
    response = await send(token);
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text ? safeParse(text) : null;

  if (!response.ok) {
    const envelope = (payload as { error?: Record<string, unknown> } | null)?.error;
    throw new ApiError(
      response.status,
      typeof envelope?.code === 'string' ? envelope.code : 'error',
      typeof envelope?.message === 'string'
        ? envelope.message
        : `Request failed (${response.status})`,
      Array.isArray(envelope?.details) ? (envelope.details as FieldFailure[]) : undefined,
      typeof envelope?.requestId === 'string' ? envelope.requestId : undefined,
    );
  }

  return payload as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * Uploads bytes straight to object storage using a presigned URL.
 *
 * Deliberately does NOT go through `request` — this call does not touch the
 * API at all. The bytes go directly to storage, which is why a 60 MB upload on
 * a poor connection never occupies an API request handler.
 *
 * The headers from the presign response must be sent back **verbatim**. They
 * are part of the signature, including the checksum, which storage verifies
 * itself: bytes that disagree with the declared digest are rejected by the
 * store rather than trusted and checked later.
 */
export async function uploadToStorage(
  presigned: { url: string; headers: Record<string, string> },
  file: Blob,
  onProgress?: (fraction: number) => void,
): Promise<void> {
  // XHR rather than fetch, purely because fetch still has no upload progress
  // events. On a mobile connection a 60 MB upload with no progress bar reads as
  // a frozen app, and people close the tab.
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presigned.url, true);

    for (const [header, value] of Object.entries(presigned.headers)) {
      xhr.setRequestHeader(header, value);
    }

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) onProgress(event.loaded / event.total);
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new UserFacingError(`The upload didn't go through (error ${xhr.status}). Please try again.`));
    };

    xhr.onerror = () => reject(new UserFacingError("The upload didn't go through. Check your connection and try again."));
    xhr.onabort = () => reject(new UserFacingError('The upload was cancelled.'));

    xhr.send(file);
  });
}
